import {
  throwError as observableThrowError,
  Observable,
  Subject,
  BehaviorSubject,
  Subscription,
  interval,
} from 'rxjs'
import {HttpErrorResponse} from '@angular/common/http'

import {NotificationService} from '@app/core/services/notification.service'
import {LoggerService} from '@app/core/services/logger.service'
import {finalize, take, tap, filter, catchError} from 'rxjs/operators'

export abstract class GenericManagerService<T> {
  private autoReloadSub: Subscription = null

  protected loadingSubject: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false)
  protected errorSubject: BehaviorSubject<Error> = new BehaviorSubject<Error>(
    null,
  )

  protected itemSubject: BehaviorSubject<T> = new BehaviorSubject<T>(null)

  public readonly item: Observable<T> = this.itemSubject.asObservable()
  public readonly loading: Observable<boolean> =
    this.loadingSubject.asObservable()
  public readonly error: Observable<Error> = this.errorSubject.asObservable()

  protected loadedId: any = null

  constructor(
    protected notify: NotificationService,
    protected logger: LoggerService,
  ) {}

  get id(): any {
    return this.loadedId
  }

  get value(): T {
    return this.itemSubject.value
  }

  getLoaded(): Observable<T> {
    return this.item.pipe(
      filter((val) => val !== null),
      take(1),
    )
  }

  private loadInternal(id: any): Subject<T> {
    this.loadingSubject.next(true)
    this.loadedId = id

    let sub = this.retrieve(id)
      .pipe(
        catchError((err: Error) => this.handleError(err)),
        finalize(() => sub.unsubscribe()),
      )
      .subscribe(
        (result) => {
          this.itemSubject.next(result)

          this.errorSubject.next(null)
          this.loadingSubject.next(false)
        },
        (err) => {
          this.errorSubject.next(err)
          this.loadingSubject.next(false)
        },
      )

    return this.itemSubject
  }

  load(id: any): Subject<T> {
    this.itemSubject.next(null)
    return this.loadInternal(id)
  }
  // this is sometimes called in the app with an ID - that wont work (linting hello?), so i made an reloadID call instead
  reload(): Subject<T> {
    return this.loadInternal(this.loadedId)
  }
  reloadID(id: string): Subject<T> {
    return this.loadInternal(id)
  }

  clear(): void {
    this.itemSubject.next(null)
  }

  autoRefresh(timeout?: number): void {
    if (this.autoReloadSub) {
      this.autoReloadSub.unsubscribe()
    }
    if (timeout) {
      this.autoReloadSub = interval(timeout).subscribe((res) => {
        if (this.loadedId) {
          let sub = this.retrieve(this.loadedId)
            .pipe(
              catchError((err: Error) => this.handleError(err)),
              finalize(() => sub.unsubscribe()),
            )
            .subscribe(
              (result) => {
                this.itemSubject.next(result)
              },
              (err) => {
                this.logger.log('auto reload failed : ' + err)
              },
            )
        }
      })
    }
  }

  refresh(): void {
    let sub = this.retrieve(this.loadedId)
      .pipe(
        catchError((err: Error) => this.handleError(err)),
        finalize(() => sub.unsubscribe()),
      )
      .subscribe(
        (result) => {
          this.itemSubject.next(result)
        },
        (err) => {
          this.logger.log('Refresh failed : ' + err)
        },
      )
  }

  update(data: T): Observable<T> {
    if (!this.loadedId) {
      return observableThrowError(new Error('no id has been loaded yet'))
    }
    this.loadingSubject.next(true)
    return this.doUpdate(this.loadedId, data).pipe(
      tap(
        (result) => {
          this.itemSubject.next(result)

          this.loadingSubject.next(false)
          this.errorSubject.next(null)
        },
        (err) => {
          this.errorSubject.next(err)
          this.loadingSubject.next(false)
        },
      ),
      catchError((err: Error) => this.handleError(err)),
    )
  }

  create(data: T | any): Observable<T> {
    this.loadingSubject.next(true)
    return this.doCreate(data).pipe(
      tap((result) => {
        this.loadedId = this.retrieveId(result)

        this.itemSubject.next(result)

        this.loadingSubject.next(false)
        this.errorSubject.next(null)
      }),
      catchError((err: Error) => {
        this.errorSubject.next(err)
        this.loadingSubject.next(false)
        return this.handleError(err)
      }),
    )
  }

  delete(force = false): Observable<void> {
    this.loadingSubject.next(true)
    return this.doDelete(this.loadedId, force).pipe(
      tap(
        () => {
          this.loadedId = null
          this.itemSubject.next(null)

          this.loadingSubject.next(false)
          this.errorSubject.next(null)
        },
        (err) => {
          this.errorSubject.next(err)
          this.loadingSubject.next(false)
        },
      ),
      catchError((err: Error) => this.handleError(err)),
    )
  }

  protected abstract retrieveId(data: T): any

  protected abstract retrieve(id: any): Observable<T>

  protected abstract doUpdate(id: any, data: T): Observable<T>

  protected abstract doCreate(data: T | any): Observable<T>

  protected abstract doDelete(id: any, force?: boolean): Observable<void>

  protected handleError(err: Error): Observable<any> {
    if (err instanceof HttpErrorResponse) {
      if (err.error) {
        return observableThrowError(new Error(err.error.description))
      } else {
        err = new Error('Error: ' + err.status + ' => ' + err.message)
      }
    }
    // log the error
    this.logger.log(err)

    // notify the service
    this.notify.error(err.message)
    return observableThrowError(new Error('Unexpected error'))
  }
}
